<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useUserStore } from '@/stores/user';
import {
    createNotification,
    deleteNotification,
    listNotifications,
} from '@/api/notifications';
import type {
    NotificationSubscription,
    NotificationKind,
} from '@/api/types';

const props = defineProps<{ siteId: string }>();

const userStore = useUserStore();
const subs = ref<NotificationSubscription[]>([]);
const loading = ref(true);
const error = ref<string | null>(null);
const busy = ref(false);

const DEFAULT_EXTRA_EMAIL = 'delivery@bazeport.com';

const offlineHours = ref(2);
const extraEmail = ref('');
const extraKind = ref<NotificationKind>('next_connect');
const extraHours = ref(2);

const ownEmail = computed(() => userStore.identity?.email?.toLowerCase() ?? '');
const isInternal = computed(() => userStore.identity?.isInternal ?? false);

const ownNextConnect = computed(() =>
    subs.value.find((s) => s.kind === 'next_connect' && s.recipientEmail.toLowerCase() === ownEmail.value),
);
const ownOffline = computed(() =>
    subs.value.find((s) => s.kind === 'offline' && s.recipientEmail.toLowerCase() === ownEmail.value),
);

const otherRecipientSubs = computed(() =>
    subs.value.filter((s) => s.recipientEmail.toLowerCase() !== ownEmail.value),
);

async function refresh() {
    try {
        loading.value = true;
        subs.value = await listNotifications(props.siteId);
        const offline = subs.value.find(
            (s) => s.kind === 'offline' && s.recipientEmail.toLowerCase() === ownEmail.value,
        );
        if (offline?.thresholdHours) offlineHours.value = offline.thresholdHours;
        maybePrefillExtra();
    } catch (e) {
        error.value = (e as Error).message;
    } finally {
        loading.value = false;
    }
}

function maybePrefillExtra() {
    if (!isInternal.value) return;
    if (extraEmail.value) return;
    const exists = subs.value.some(
        (s) => s.recipientEmail.toLowerCase() === DEFAULT_EXTRA_EMAIL,
    );
    if (!exists) extraEmail.value = DEFAULT_EXTRA_EMAIL;
}

async function toggleNextConnect(want: boolean) {
    if (busy.value) return;
    busy.value = true;
    error.value = null;
    try {
        if (want && !ownNextConnect.value) {
            await createNotification(props.siteId, { kind: 'next_connect' });
        } else if (!want && ownNextConnect.value) {
            await deleteNotification(props.siteId, ownNextConnect.value.id);
        }
        await refresh();
    } catch (e) {
        error.value = (e as Error).message;
    } finally {
        busy.value = false;
    }
}

async function toggleOffline(want: boolean) {
    if (busy.value) return;
    busy.value = true;
    error.value = null;
    try {
        if (want) {
            if (ownOffline.value) {
                await deleteNotification(props.siteId, ownOffline.value.id);
            }
            await createNotification(props.siteId, {
                kind: 'offline',
                thresholdHours: offlineHours.value,
            });
        } else if (ownOffline.value) {
            await deleteNotification(props.siteId, ownOffline.value.id);
        }
        await refresh();
    } catch (e) {
        error.value = (e as Error).message;
    } finally {
        busy.value = false;
    }
}

async function addExtraRecipient() {
    if (busy.value || !extraEmail.value) return;
    busy.value = true;
    error.value = null;
    try {
        await createNotification(props.siteId, {
            kind: extraKind.value,
            recipientEmail: extraEmail.value.trim(),
            thresholdHours: extraKind.value === 'offline' ? extraHours.value : undefined,
        });
        extraEmail.value = '';
        await refresh();
    } catch (e) {
        error.value = (e as Error).message;
    } finally {
        busy.value = false;
    }
}

async function removeSub(id: string) {
    if (busy.value) return;
    busy.value = true;
    error.value = null;
    try {
        await deleteNotification(props.siteId, id);
        await refresh();
    } catch (e) {
        error.value = (e as Error).message;
    } finally {
        busy.value = false;
    }
}

onMounted(refresh);
</script>

<template>
    <section class="bg-bp-panel border border-bp-border rounded p-3 sm:p-4 text-sm">
        <h2 class="text-sm font-semibold uppercase text-slate-400 mb-3 tracking-wide">Notifications</h2>

        <div v-if="error" class="mb-3 bg-red-900/40 border border-red-700 px-3 py-2 rounded">{{ error }}</div>
        <div v-if="loading" class="text-slate-400">Loading…</div>

        <div v-else class="space-y-4">
            <label class="flex items-start gap-2">
                <input
                    type="checkbox"
                    class="mt-0.5 flex-shrink-0"
                    :checked="!!ownNextConnect"
                    :disabled="busy"
                    @change="(e) => toggleNextConnect((e.target as HTMLInputElement).checked)"
                />
                <span>Email me the next time this site comes online</span>
            </label>

            <div class="space-y-2">
                <label class="flex items-start gap-2">
                    <input
                        type="checkbox"
                        class="mt-0.5 flex-shrink-0"
                        :checked="!!ownOffline"
                        :disabled="busy"
                        @change="(e) => toggleOffline((e.target as HTMLInputElement).checked)"
                    />
                    <span>Email me when this site has been offline for more than</span>
                </label>
                <div class="flex flex-wrap items-center gap-2 pl-6">
                    <input
                        v-model.number="offlineHours"
                        type="number"
                        min="1"
                        max="720"
                        class="w-20 bg-bp-bg border border-bp-border rounded px-2 py-1 focus:ring focus:ring-bp-accent focus:outline-none"
                        :disabled="busy"
                    />
                    <span>hours</span>
                    <button
                        v-if="ownOffline && ownOffline.thresholdHours !== offlineHours"
                        class="text-bp-link hover:text-bp-link-hover text-xs underline"
                        :disabled="busy"
                        @click="toggleOffline(true)"
                    >update threshold</button>
                </div>
            </div>

            <div v-if="isInternal" class="pt-3 border-t border-bp-border space-y-3">
                <h3 class="text-xs uppercase text-slate-400 tracking-wide">Other recipients</h3>
                <ul v-if="otherRecipientSubs.length" class="space-y-2 sm:space-y-1">
                    <li
                        v-for="s in otherRecipientSubs"
                        :key="s.id"
                        class="flex items-center justify-between gap-2 flex-wrap sm:flex-nowrap"
                    >
                        <span class="font-mono text-xs break-all min-w-0">{{ s.recipientEmail }}</span>
                        <span class="text-xs text-slate-400 flex-shrink-0">
                            {{ s.kind === 'offline' ? `offline > ${s.thresholdHours}h` : 'on next connect' }}
                        </span>
                        <button
                            class="text-red-400 text-xs underline flex-shrink-0"
                            :disabled="busy"
                            @click="removeSub(s.id)"
                        >remove</button>
                    </li>
                </ul>
                <p v-else class="text-slate-500 text-xs">No additional recipients configured.</p>

                <div class="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto_auto] gap-2 items-center">
                    <input
                        v-model="extraEmail"
                        type="email"
                        placeholder="ops@customer.com"
                        class="bg-bp-bg border border-bp-border rounded px-2 py-1.5 focus:ring focus:ring-bp-accent focus:outline-none min-w-0"
                        :disabled="busy"
                    />
                    <select
                        v-model="extraKind"
                        class="bg-bp-bg border border-bp-border rounded px-2 py-1.5"
                        :disabled="busy"
                    >
                        <option value="offline">offline</option>
                        <option value="next_connect">next connect</option>
                    </select>
                    <input
                        v-if="extraKind === 'offline'"
                        v-model.number="extraHours"
                        type="number"
                        min="1"
                        max="720"
                        class="w-20 bg-bp-bg border border-bp-border rounded px-2 py-1.5 focus:ring focus:ring-bp-accent focus:outline-none"
                        :disabled="busy"
                    />
                    <button
                        class="bg-bp-accent hover:bg-bp-accent-hover text-white text-xs px-3 py-1.5 rounded transition-colors disabled:opacity-50"
                        :disabled="busy || !extraEmail"
                        @click="addExtraRecipient"
                    >Add</button>
                </div>
            </div>
        </div>
    </section>
</template>
